import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useClient } from "@/context/ClientContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Upload, Search, RefreshCw, Download, ArrowUpDown, ExternalLink, BarChart3, Settings2,
  FileDown, ChevronDown, UserCog, SlidersHorizontal,
} from "lucide-react";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuCheckboxItem,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { useExport } from "@/context/ExportContext";
import { downloadExport } from "@/lib/downloadExport";
import { useDispositions } from "@/hooks/useDispositions";
import { useAuth } from "@/context/AuthContext";
import LeadsUploadDialog from "@/components/leads/LeadsUploadDialog";
import ReassignDialog from "@/components/leads/ReassignDialog";
import ExportColumnsDialog from "@/components/leads/ExportColumnsDialog";
import GroupedMultiSelect from "@/components/leads/GroupedMultiSelect";

function DispositionChip({ code, meta }) {
  if (!code) return <span className="text-xs text-slate-400">—</span>;
  const color = meta?.color;
  const style = color
    ? { color, borderColor: `${color}66`, backgroundColor: `${color}14` }
    : {};
  return (
    <Badge
      variant="outline"
      style={style}
      className={`text-[10px] font-semibold max-w-[110px] truncate ${color ? "" : "bg-slate-100 text-slate-700 border-slate-200"}`}
      title={meta?.label || code}
      data-testid={`disposition-${code}`}
    >
      {code}
    </Badge>
  );
}

const NO_DISPOSITION = "__none__";
const SORT_KEYS = ["created_at", "customer_id", "customer_name", "classification", "attempts", "updated_at", "disposition"];

export default function Leads() {
  const { active, hasModule } = useClient();
  const { hasPerm } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [rows, setRows] = useState([]);
  const [selected, setSelected] = useState(() => new Set());
  const [reassignOpen, setReassignOpen] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(25);
  const [q, setQ] = useState(searchParams.get("q") || "");
  const [disposition, setDisposition] = useState(
    () => (searchParams.get("disposition") ? searchParams.get("disposition").split(",") : []));
  const [hqAvailable, setHqAvailable] = useState(searchParams.get("hq") || "all");
  const [assignee, setAssignee] = useState(
    () => (searchParams.get("assignee") ? searchParams.get("assignee").split(",") : []));
  // Drill-down-only filters (set by dashboard links, no toolbar UI)
  const [classification, setClassification] = useState(searchParams.get("classification") ?? "");
  const [attemptsFilter, setAttemptsFilter] = useState(searchParams.get("attempts") ?? "");
  const [assignees, setAssignees] = useState([]);
  const [exporting, setExporting] = useState(false);
  const [sort, setSort] = useState(() => ({
    key: SORT_KEYS.includes(searchParams.get("sort")) ? searchParams.get("sort") : "created_at",
    dir: searchParams.get("order") === "asc" ? "asc" : "desc",
  }));
  const [loading, setLoading] = useState(true);
  const [uploadOpen, setUploadOpen] = useState(false);
  const { setHandler } = useExport();
  const { options: dispositions, byValue: dispositionByValue } = useDispositions();

  // Dynamic Field Management: capture-field filters + optional table columns.
  const [fields, setFields] = useState([]);
  const [dropdowns, setDropdowns] = useState({});
  const [fieldFilters, setFieldFilters] = useState(() => {
    try {
      const parsed = JSON.parse(searchParams.get("ff") || "{}");
      return Object.fromEntries(Object.entries(parsed).map(([k, v]) => [k, Array.isArray(v) ? v : String(v).split(",")]));
    } catch { return {}; }
  });   // {field_key: value}
  const colStoreKey = `kippu_leads_cols_${active?.slug || "default"}`;
  const [extraCols, setExtraCols] = useState([]);

  const isLeadsClient = hasModule("leads");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page, page_size: pageSize,
        sort: sort.key, order: sort.dir,
      };
      if (q.trim()) params.q = q.trim();
      if (disposition.length) params.disposition = disposition.join(",");
      if (hqAvailable !== "all") params.hq_available = hqAvailable === "yes";
      if (assignee.length) params.assignee = assignee.join(",");
      if (classification !== "") params.classification = classification;
      if (attemptsFilter !== "") params.attempts = attemptsFilter;
      const activeFF = Object.fromEntries(
        Object.entries(fieldFilters)
          .map(([k, v]) => [k, Array.isArray(v) ? v.join(",") : v])
          .filter(([, v]) => v && v !== "all"));
      if (Object.keys(activeFF).length) params.ff = JSON.stringify(activeFF);
      const { data } = await api.get("/leads", { params });
      setRows(data.leads || []);
      setTotal(data.total || 0);
      setSelected(new Set());
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load leads");
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, sort, q, disposition, hqAvailable, assignee, classification, attemptsFilter, fieldFilters]);

  useEffect(() => {
    if (isLeadsClient) load();
  }, [isLeadsClient, load]);

  // Keep disposition filter + sort in the URL so the view is shareable and survives refresh.
  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    if (disposition.length) next.set("disposition", disposition.join(",")); else next.delete("disposition");
    if (sort.key !== "created_at" || sort.dir !== "desc") { next.set("sort", sort.key); next.set("order", sort.dir); }
    else { next.delete("sort"); next.delete("order"); }
    if (next.toString() !== searchParams.toString()) setSearchParams(next, { replace: true });
  }, [disposition, sort]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isLeadsClient) return;
    api.get("/leads/assignees")
      .then((r) => setAssignees(r.data.assignees || []))
      .catch(() => {});
  }, [isLeadsClient, active?.slug]);

  // Load Field-Management config + dropdown catalogue for filters/columns.
  useEffect(() => {
    if (!isLeadsClient) return;
    Promise.all([
      api.get("/lead-fields").catch(() => ({ data: { fields: [] } })),
      api.get("/lead-dropdowns").catch(() => ({ data: { grouped: {} } })),
    ]).then(([f, d]) => {
      setFields((f.data.fields || []).filter((x) => x.visible !== false));
      setDropdowns(d.data?.grouped || {});
    });
  }, [isLeadsClient, active?.slug]);

  // Restore per-tenant table column choices.
  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(colStoreKey) || "[]");
      setExtraCols(Array.isArray(saved) ? saved : []);
    } catch { setExtraCols([]); }
  }, [colStoreKey]);

  // Capture-section SELECT fields become slice-by filters (disposition already
  // has its own dedicated control, so exclude it here).
  const captureFilters = useMemo(
    () => fields.filter((f) => f.section === "capture" && f.type === "select" && f.key !== "disposition"),
    [fields]);

  const toggleExtraCol = (key) => setExtraCols((cols) => {
    const next = cols.includes(key) ? cols.filter((k) => k !== key) : [...cols, key];
    localStorage.setItem(colStoreKey, JSON.stringify(next));
    return next;
  });

  // Field-value resolver for dynamically added table columns.
  const resolveField = useCallback((lead, field) => {
    const k = field.key;
    const NESTED = { poc_name: ["poc", "name"], poc_phone: ["poc", "phone"], poc_email: ["poc", "email"],
                     hq_name: ["head_office", "name"], hq_phone: ["head_office", "phone"], hq_email: ["head_office", "email"] };
    const TOP = ["employee_count", "sector", "call_language", "remarks", "follow_up_at", "disposition"];
    if (NESTED[k]) return (lead[NESTED[k][0]] || {})[NESTED[k][1]] || "";
    if (TOP.includes(k)) return lead[k] || "";
    return (lead.custom || {})[k] || "";
  }, []);

  const extraFieldDefs = useMemo(
    () => extraCols.map((k) => fields.find((f) => f.key === k)).filter(Boolean),
    [extraCols, fields]);

  const exportLeads = useCallback(async (format) => {
    setExporting(true);
    try {
      const params = { format, sort: sort.key, order: sort.dir };
      if (q.trim()) params.q = q.trim();
      if (disposition.length) params.disposition = disposition.join(",");
      if (hqAvailable !== "all") params.hq_available = hqAvailable === "yes";
      if (assignee.length) params.assignee = assignee.join(",");
      if (classification !== "") params.classification = classification;
      if (attemptsFilter !== "") params.attempts = attemptsFilter;
      const stamp = new Date().toISOString().slice(0, 10);
      await downloadExport("/leads/export", params,
        `kippu-leads-${active?.slug || "export"}-${stamp}.${format}`);
      toast.success(`Lead export downloaded (.${format}) — filtered view, all fields`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Export failed");
    } finally {
      setExporting(false);
    }
  }, [q, disposition, hqAvailable, assignee, classification, attemptsFilter, sort, active?.slug]);

  const exportDispoHistory = useCallback(async (format) => {
    setExporting(true);
    try {
      const stamp = new Date().toISOString().slice(0, 10);
      await downloadExport("/leads/disposition-history/export", { format },
        `kippu-disposition-history-${active?.slug || "export"}-${stamp}.${format}`);
      toast.success(`Disposition history downloaded (.${format}) — every change per lead`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Export failed");
    } finally {
      setExporting(false);
    }
  }, [active?.slug]);

  // Wire the topbar "Export CSV" button to this page's filtered export.
  useEffect(() => {
    if (!isLeadsClient) return undefined;
    setHandler(() => exportLeads("csv"));
    return () => setHandler(null);
  }, [isLeadsClient, exportLeads, setHandler]);

  const toggleSort = (key) => setSort((s) =>
    s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" }
  );

  const pageCount = Math.max(1, Math.ceil(total / pageSize));

  const canReassign = hasPerm("leads.reassign");
  const isCampaign = active?.kind !== "leads";   // Starlink outbound campaign (leads module on a ticketing tenant)
  const colCount = (canReassign ? 1 : 0) + (isCampaign ? 8 : 10) + extraFieldDefs.length;
  const labelFor = (ddKey, v) => ((dropdowns[ddKey] || []).find((o) => o.value === v) || {}).label || v || "";
  const roundRobin = async () => {
    try {
      const { data } = await api.post("/leads/assign", { lead_ids: Array.from(selected), reason: "round_robin" });
      toast.success(`Distributed ${data.assigned ?? selected.size} lead(s) round-robin`);
      setSelected(new Set());
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Round-robin assignment failed");
    }
  };
  const allOnPageSelected = rows.length > 0 && rows.every((r) => selected.has(r.id));
  const toggleRow = (id) => setSelected((s) => {
    const next = new Set(s);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });
  const togglePage = () => setSelected((s) => {
    if (allOnPageSelected) return new Set();
    return new Set(rows.map((r) => r.id));
  });

  const downloadTemplate = async () => {
    try {
      const res = await api.get("/leads/template", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "kippu-leads-template.xlsx";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Template download failed");
    }
  };

  if (!isLeadsClient) {
    return (
      <div className="min-h-[60vh] grid place-items-center text-slate-500 text-sm" data-testid="leads-blocked">
        Leads are not enabled for “{active?.name || "this client"}”.
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="leads-page">
      {/* Toolbar */}
      <Card className="p-3">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[260px]">
            <Search className="w-3.5 h-3.5 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
            <Input
              value={q}
              onChange={(e) => { setQ(e.target.value); setPage(1); }}
              placeholder="Search by Customer ID, name, or phone"
              className="pl-8 h-8 text-sm"
              data-testid="leads-search"
            />
          </div>

          <GroupedMultiSelect
            value={disposition}
            onChange={(v) => { setDisposition(v); setPage(1); }}
            options={dispositions}
            label="Dispositions"
            allLabel="All dispositions"
            testid="filter-disposition"
            width="w-[210px]"
            pinned={{ value: NO_DISPOSITION, label: "No disposition yet", color: "#94A3B8" }}
          />

          <Select value={hqAvailable} onValueChange={(v) => { setHqAvailable(v); setPage(1); }}>
            <SelectTrigger className="h-8 text-xs w-[140px]" data-testid="filter-hq">
              <SelectValue placeholder="HQ" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">HQ · Any</SelectItem>
              <SelectItem value="yes">HQ · Yes</SelectItem>
              <SelectItem value="no">HQ · No</SelectItem>
            </SelectContent>
          </Select>

          <GroupedMultiSelect
            value={assignee}
            onChange={(v) => { setAssignee(v); setPage(1); }}
            options={[{ value: "__unassigned__", label: "Unassigned" },
                      ...assignees.map((a) => ({ value: a, label: a }))]}
            label="Assignees"
            allLabel="All assignees"
            testid="filter-assignee"
            width="w-[180px]"
          />

          <Button variant="outline" size="sm" onClick={load} className="h-8" data-testid="leads-refresh">
            <RefreshCw className="w-3.5 h-3.5" />
          </Button>

          {/* Dynamic capture-field filters (Area, Sector, …) */}
          {captureFilters.map((f) => (
            <GroupedMultiSelect
              key={f.key}
              value={Array.isArray(fieldFilters[f.key]) ? fieldFilters[f.key] : []}
              onChange={(v) => { setFieldFilters((s) => ({ ...s, [f.key]: v })); setPage(1); }}
              options={(dropdowns[f.dropdown_key] || [])
                .filter((o) => o.active !== false)
                .map((o) => ({ value: o.value, label: o.label || o.value, color: o.color }))}
              label={f.label}
              allLabel={`All ${f.label}`}
              testid={`filter-field-${f.key}`}
              width="w-[170px]"
            />
          ))}

          {/* Table column picker (dynamic fields as extra columns) */}
          {fields.length > 0 && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="h-8" data-testid="table-columns-btn">
                  <SlidersHorizontal className="w-3.5 h-3.5 mr-1.5" /> Table Columns
                  {extraCols.length > 0 && (
                    <Badge variant="outline" className="ml-1.5 h-4 px-1 text-[9px]">{extraCols.length}</Badge>
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="max-h-80 overflow-y-auto w-56">
                <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-slate-400">
                  Add fields to the table
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                {fields.map((f) => (
                  <DropdownMenuCheckboxItem
                    key={f.key}
                    checked={extraCols.includes(f.key)}
                    onCheckedChange={() => toggleExtraCol(f.key)}
                    onSelect={(e) => e.preventDefault()}
                    data-testid={`table-col-${f.key}`}
                  >
                    {f.label}
                  </DropdownMenuCheckboxItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          {(classification !== "" || attemptsFilter !== "") && (
            <Badge
              variant="outline"
              className="h-8 px-2 cursor-pointer text-[10px] text-indigo-700 border-indigo-200 bg-indigo-50"
              onClick={() => { setClassification(""); setAttemptsFilter(""); setPage(1); }}
              data-testid="clear-drill-filters"
              title="Filters applied from a dashboard drill-down — click to clear"
            >
              {classification !== "" && `Class ${classification} `}
              {attemptsFilter !== "" && `Attempts ${attemptsFilter} `}✕
            </Badge>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="h-8" disabled={exporting} data-testid="leads-export-btn">
                <FileDown className={`w-3.5 h-3.5 mr-1.5 ${exporting ? "animate-pulse" : ""}`} />
                {exporting ? "Exporting…" : "Export"}
                <ChevronDown className="w-3 h-3 ml-1 text-slate-400" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => exportLeads("csv")} data-testid="leads-export-csv">
                <FileDown className="w-3.5 h-3.5 mr-2 text-sky-600" /> Export CSV
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => exportLeads("xlsx")} data-testid="leads-export-xlsx">
                <FileDown className="w-3.5 h-3.5 mr-2 text-emerald-600" /> Export Excel
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => exportDispoHistory("csv")} data-testid="leads-export-dispo-history-csv">
                <FileDown className="w-3.5 h-3.5 mr-2 text-amber-600" /> Disposition History CSV
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => exportDispoHistory("xlsx")} data-testid="leads-export-dispo-history-xlsx">
                <FileDown className="w-3.5 h-3.5 mr-2 text-amber-600" /> Disposition History Excel
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <ExportColumnsDialog />
          {isCampaign && (
            <>
              <Button variant="outline" size="sm" onClick={() => navigate("/leads/dashboard")} className="h-8" data-testid="campaign-dashboard-btn">
                <BarChart3 className="w-3.5 h-3.5 mr-1.5" /> Campaign dashboard
              </Button>
              <Button variant="outline" size="sm" onClick={() => navigate("/leads/settings")} className="h-8" data-testid="campaign-settings-btn">
                <Settings2 className="w-3.5 h-3.5 mr-1.5" /> Campaign settings
              </Button>
              <Button variant="outline" size="sm" onClick={() => downloadExport("/leads/export/campaign-original", {}, "campaign-results.xlsx").then(() => toast.success("Client-layout export downloaded")).catch(() => toast.error("Export failed"))}
                      className="h-8" data-testid="campaign-original-export-btn">
                <FileDown className="w-3.5 h-3.5 mr-1.5" /> Client layout
              </Button>
            </>
          )}
          <Button variant="outline" size="sm" onClick={downloadTemplate} className="h-8" data-testid="download-template">
            <Download className="w-3.5 h-3.5 mr-1.5" /> Template
          </Button>
          <Button
            size="sm"
            onClick={() => setUploadOpen(true)}
            className="h-8 bg-indigo-600 hover:bg-indigo-700 text-white"
            data-testid="upload-leads-btn"
          >
            <Upload className="w-3.5 h-3.5 mr-1.5" /> Upload Leads
          </Button>
        </div>
      </Card>

      {/* Bulk selection bar */}
      {canReassign && selected.size > 0 && (
        <Card className="p-2.5 border-indigo-200 bg-indigo-50/50" data-testid="bulk-bar">
          <div className="flex items-center gap-3">
            <span className="text-xs font-medium text-indigo-800" data-testid="bulk-count">
              {selected.size} lead{selected.size === 1 ? "" : "s"} selected
            </span>
            <Button size="sm" className="h-7 bg-indigo-600 hover:bg-indigo-700 text-xs"
                    onClick={() => setReassignOpen(true)} data-testid="bulk-reassign-btn">
              <UserCog className="w-3.5 h-3.5 mr-1.5" /> Reassign selected
            </Button>
            <Button size="sm" variant="outline" className="h-7 text-xs"
                    onClick={roundRobin} data-testid="bulk-round-robin-btn">
              <ArrowUpDown className="w-3.5 h-3.5 mr-1.5" /> Distribute round-robin
            </Button>
            <Button variant="ghost" size="sm" className="h-7 text-xs text-slate-500"
                    onClick={() => setSelected(new Set())} data-testid="bulk-clear">
              Clear
            </Button>
          </div>
        </Card>
      )}

      {/* Table */}
      <Card className="overflow-hidden">
        <Table className="text-xs">
          <TableHeader className="bg-slate-50">
            <TableRow>
              {canReassign && (
                <TableHead className="w-8">
                  <Checkbox checked={allOnPageSelected} onCheckedChange={togglePage}
                            data-testid="select-all-leads" aria-label="Select all on page" />
                </TableHead>
              )}
              <SortableTh label={isCampaign ? "Mobile" : "Customer ID"} k="customer_id" sort={sort} onClick={toggleSort} />
              <SortableTh label={isCampaign ? "Customer" : "Customer Name"} k="customer_name" sort={sort} onClick={toggleSort} />
              {isCampaign ? (
                <>
                  <TableHead>Order(s)</TableHead>
                  <TableHead>Type / Status</TableHead>
                </>
              ) : (
                <>
                  <TableHead>Contact</TableHead>
                  <TableHead>Contact #s</TableHead>
                  <SortableTh label="Class." k="classification" sort={sort} onClick={toggleSort} align="right" />
                  <TableHead>HQ</TableHead>
                </>
              )}
              <SortableTh label={isCampaign ? "Result" : "Disp."} k="disposition" sort={sort} onClick={toggleSort} />
              <TableHead>Assignee</TableHead>
              <SortableTh label="Attempts" k="attempts" sort={sort} onClick={toggleSort} align="right" />
              {extraFieldDefs.map((f) => (
                <TableHead key={f.key} data-testid={`col-head-${f.key}`}>{f.label}</TableHead>
              ))}
              <TableHead className="w-8"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={colCount} className="text-center text-slate-400 py-8">Loading…</TableCell></TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={colCount} className="text-center text-slate-400 py-12">
                  No leads yet. Click <span className="font-medium text-slate-600">Upload Leads</span> to get started.
                </TableCell>
              </TableRow>
            ) : (
              rows.map((l) => (
                <TableRow
                  key={l.id}
                  className="hover:bg-slate-50 cursor-pointer"
                  onClick={() => navigate(`/leads/${l.id}`)}
                  data-testid={`lead-row-${l.customer_id}`}
                >
                  {canReassign && (
                    <TableCell onClick={(e) => e.stopPropagation()} className="w-8">
                      <Checkbox checked={selected.has(l.id)} onCheckedChange={() => toggleRow(l.id)}
                                data-testid={`select-lead-${l.customer_id}`} aria-label="Select lead" />
                    </TableCell>
                  )}
                  <TableCell className="font-mono text-[11px] text-indigo-700">{isCampaign ? `+${l.customer_id}` : l.customer_id}</TableCell>
                  <TableCell className="font-medium text-slate-900 max-w-[220px] truncate">{l.customer_name}</TableCell>
                  {isCampaign && (
                    <>
                      <TableCell className="text-[11px] text-slate-600 max-w-[200px] truncate" data-testid={`orders-${l.customer_id}`}>
                        {(l.campaign?.order_numbers || []).join(", ") || l.campaign?.items_summary || <span className="text-slate-300">—</span>}
                      </TableCell>
                      <TableCell data-testid={`type-status-${l.customer_id}`}>
                        <div className="flex flex-col gap-0.5">
                          <Badge variant="outline" className="text-[9px] w-fit">{labelFor("lead_type", l.custom?.cf_lead_type)}</Badge>
                          <span className="text-[10px] text-slate-500 max-w-[220px] truncate" title={labelFor("lead_status", l.custom?.cf_lead_status)}>
                            {labelFor("lead_status", l.custom?.cf_lead_status)}
                          </span>
                        </div>
                      </TableCell>
                    </>
                  )}
                  {!isCampaign && (
                  <TableCell className="text-slate-600 max-w-[160px] truncate">{l.contact_name || <span className="text-slate-300">—</span>}</TableCell>
                  )}
                  {!isCampaign && (
                  <TableCell>
                    <div className="flex flex-col gap-0.5">
                      {(l.numbers || []).slice(0, 3).map((n) => (
                        <span
                          key={n.e164}
                          className={`text-[11px] font-mono ${n.wrong_number ? "line-through text-slate-400" : "text-slate-700"}`}
                          title={n.wrong_number ? "Wrong Number — excluded from dialing" : ""}
                        >
                          {n.e164}
                        </span>
                      ))}
                      {l.refresh_count > 0 && (
                        <Badge variant="outline" className="text-[9px] h-4 px-1 w-fit text-indigo-700 border-indigo-200 bg-indigo-50"
                               title={`Numbers refreshed by client · ${(l.last_refresh_at || "").slice(0, 10)}`}
                               data-testid={`refreshed-marker-${l.customer_id}`}>
                          refreshed
                        </Badge>
                      )}
                    </div>
                  </TableCell>
                  )}
                  {!isCampaign && (
                  <TableCell className="text-right">
                    <Badge variant="outline" className="font-mono text-[10px]">
                      {l.classification}
                    </Badge>
                  </TableCell>
                  )}
                  {!isCampaign && (
                  <TableCell>
                    <span className={`text-[10px] font-semibold ${l.head_office?.has ? "text-emerald-700" : "text-slate-400"}`}>
                      {l.head_office?.has ? "YES" : "NO"}
                    </span>
                  </TableCell>
                  )}
                  <TableCell><DispositionChip code={l.disposition} meta={dispositionByValue[l.disposition]} /></TableCell>
                  <TableCell className="text-slate-600 max-w-[160px] truncate">
                    {l.pending_language_assignment ? (
                      <Badge variant="outline" className="text-[9px] bg-amber-50 text-amber-700 border-amber-300">
                        Arabic — pending
                      </Badge>
                    ) : (l.assigned_to || <span className="text-slate-300">unassigned</span>)}
                  </TableCell>
                  <TableCell className="text-right font-mono text-[11px]">{l.attempts || 0}</TableCell>
                  {extraFieldDefs.map((f) => {
                    const val = resolveField(l, f);
                    return (
                      <TableCell key={f.key} className="text-slate-600 max-w-[160px] truncate"
                                 data-testid={`col-cell-${f.key}-${l.customer_id}`}>
                        {val === "" || val == null ? <span className="text-slate-300">—</span> : String(val)}
                      </TableCell>
                    );
                  })}
                  <TableCell>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6"
                      onClick={(e) => { e.stopPropagation(); window.open(`/leads/${l.id}`, "_blank"); }}
                      title="Open in new tab"
                      data-testid={`lead-newtab-${l.customer_id}`}
                    >
                      <ExternalLink className="w-3 h-3" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Card>

      {/* Pagination */}
      <div className="flex items-center justify-between text-xs text-slate-500">
        <div>{total} lead{total === 1 ? "" : "s"} total</div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline" size="sm" className="h-7 px-2"
            disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}
            data-testid="leads-prev"
          >Prev</Button>
          <span data-testid="leads-page-indicator">Page {page} of {pageCount}</span>
          <Button
            variant="outline" size="sm" className="h-7 px-2"
            disabled={page >= pageCount} onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
            data-testid="leads-next"
          >Next</Button>
        </div>
      </div>

      <LeadsUploadDialog
        open={uploadOpen}
        onOpenChange={setUploadOpen}
        onComplete={() => { setUploadOpen(false); load(); }}
      />

      <ReassignDialog
        open={reassignOpen}
        onOpenChange={setReassignOpen}
        leadIds={[...selected]}
        onDone={load}
      />
    </div>
  );
}

function SortableTh({ label, k, sort, onClick, align = "left" }) {
  const active = sort.key === k;
  return (
    <TableHead
      className={`cursor-pointer select-none ${align === "right" ? "text-right" : ""}`}
      onClick={() => onClick(k)}
      data-testid={`sort-${k}`}
    >
      <span className={`inline-flex items-center gap-1 ${active ? "text-indigo-700 font-semibold" : ""}`}>
        {label}
        <ArrowUpDown className="w-3 h-3 opacity-60" />
        {active && <span className="text-[9px]">{sort.dir === "asc" ? "▲" : "▼"}</span>}
      </span>
    </TableHead>
  );
}
